import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { getDRACOLoader } from './loaderConfig';
import { applyLookMap } from '../avatar/lookMaterials';

/**
 * Render a GLB/GLTF model offscreen and capture a single front-facing frame as a
 * PNG blob. Used for avatar wardrobe thumbnails so we don't depend on external
 * (Meshy) thumbnail URLs, which expire / return malformed links. Returns null if
 * the model can't be loaded or captured (caller should fall back gracefully).
 */
export async function captureModelThumbnail(
  modelUrl: string,
  size = 384,
  /**
   * Base-colour override, for avatar "looks". Meshy's own task thumbnail is a
   * render of ITS re-exported model, which does not reliably match the texture
   * we actually apply — the tile showed a different outfit than the avatar wore
   * and made a working feature look broken. Rendering the thumbnail ourselves,
   * from our model with the real texture, is the only image that cannot lie.
   */
  lookTextureUrl?: string | null,
): Promise<Blob | null> {
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(size, size);
  renderer.setPixelRatio(1);
  renderer.setClearColor(0x000000, 0); // transparent — cards style the background
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 0.9));
  const key = new THREE.DirectionalLight(0xffffff, 1.3); key.position.set(2, 4, 3); scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.5); fill.position.set(-3, 2, 2); scene.add(fill);

  const loader = new GLTFLoader();
  try { loader.setDRACOLoader(getDRACOLoader()); } catch { /* draco optional */ }

  let blob: Blob | null = null;
  try {
    const gltf = await loader.loadAsync(modelUrl);
    const model = gltf.scene;
    scene.add(model);

    if (lookTextureUrl) {
      const tex = await new THREE.TextureLoader().loadAsync(lookTextureUrl);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = false;
      tex.needsUpdate = true;
      // This scene is private and disposed after the capture, so materials can
      // be written directly — no cloning needed here, unlike the live viewer.
      model.traverse((o: any) => {
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
        mats.forEach((m: any) => { if ('map' in m) applyLookMap(m, tex, m.map); });
      });
    }

    // Center the model at the origin, then frame it to fill the square.
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const dims = box.getSize(new THREE.Vector3());
    model.position.sub(center);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.01, 100);
    const fov = (camera.fov * Math.PI) / 180;
    const fitDim = Math.max(dims.y, dims.x) || 1;
    let dist = (fitDim / 2) / Math.tan(fov / 2);
    dist = dist * 1.25 + dims.z / 2; // padding + depth
    camera.position.set(0, 0, dist);
    camera.lookAt(0, 0, 0);

    renderer.render(scene, camera);
    blob = await new Promise<Blob | null>((res) => renderer.domElement.toBlob(res, 'image/png'));

    // Dispose GPU resources.
    model.traverse((o: any) => {
      if (o.geometry) o.geometry.dispose();
      const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
      mats.forEach((m: any) => {
        for (const k in m) { if (m[k] && (m[k] as any).isTexture) (m[k] as any).dispose(); }
        m.dispose?.();
      });
    });
  } finally {
    renderer.dispose();
    (renderer as any).forceContextLoss?.();
  }
  return blob;
}
